<script setup lang="ts">
import type { AuthoringTurn } from '../../../models/insightAuthoring.types'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'

defineProps<{ turns: readonly AuthoringTurn[] }>()
const emit = defineEmits<{ suggestFix: [warnings: AuthoringTurn['warnings']] }>()
const { t } = useI18n()
</script>

<template>
  <section
    class="authoring-turns grid gap-md"
    :aria-label="t('insights.sysadmin.authoring.turns.label')"
  >
    <div
      v-if="turns.length === 0"
      class="authoring-turns__empty p-xl text-muted text-center border border-dashed border-border rounded-md"
    >
      {{ t('insights.sysadmin.authoring.turns.empty') }}
    </div>
    <article v-for="turn in turns" :key="turn.turn_id" class="authoring-turns__turn grid gap-sm">
      <div class="authoring-turns__user p-md border border-border rounded-md bg-surface ms-xl">
        <strong>{{ t('insights.sysadmin.authoring.turns.you') }}</strong>
        <p>{{ turn.user_message }}</p>
      </div>
      <div class="authoring-turns__assistant p-md border border-border rounded-md bg-surface">
        <strong>{{ t('insights.sysadmin.authoring.turns.assistant') }}</strong>
        <p>{{ turn.notes }}</p>
        <Alert v-if="turn.warnings.length > 0" tone="warning">
          <ul>
            <li v-for="warning in turn.warnings" :key="`${warning.path}:${warning.msg}`">
              {{ warning.msg }}
            </li>
          </ul>
          <Button size="xs" variant="ghost" @click="emit('suggestFix', turn.warnings)">
            {{ t('insights.sysadmin.authoring.turns.suggestFix') }}
          </Button>
        </Alert>
      </div>
    </article>
  </section>
</template>

<style scoped>
.authoring-turns p {
  margin-block: var(--ui-space-xs) 0;
  white-space: pre-wrap;
}
</style>
